import { fromJS, Map } from 'immutable'
import { pipe, splitAt, compose, clone, prop, uniq, uniqBy } from 'ramda'
import { createReducer } from 'reducers/utils'
import {
  SOLENOIDS_RECEIVED,
  ECP_MODULES_STATE_RECEIVED,
  REMOVE_SOLENOID,
  RESET_ECP,
  ADD_SOLENOID,
  ECP_MODULE_UPDATED,
  ON_PNEUMATICS_DROP,
  ON_SOLENOID_DRAG,
  OFF_SOLENOID_DRAG,
  ADD_ECP,
  ECP_LINKED_FEATURES_RECIEVED,
  OPTIONAL_SOLENOIDS_NOT_INSTALLED,
  CLEAR_ECP_STATE,
} from 'actions/ecp'
import { toInt } from 'utils/types'
import { inclusiveRange } from 'utils/math'
import { tempIdEntity } from 'utils/identity'
import { getIn, setIn, getInOr, set } from 'utils/immutable'
import { MAX_SOLENOIDS } from '../constants/ecp'

const END_SOLENOID_PART_NUMBER = '12-33711-001'
const SOLENOID_PART_NUMBER = '12-33711-000'

const LEGACY_END_SOLENOID_PART_NUMBER = '12-29442-001'

const isEndSolenoidPartNumber = (solenoid) =>
  solenoid &&
  (solenoid.get('partNumber') === END_SOLENOID_PART_NUMBER ||
    solenoid.get('partNumber') === LEGACY_END_SOLENOID_PART_NUMBER)

const positionUtil = (slotId) => {
  const [identifier, position] = splitAt(-1, slotId)
  const formatPosition = (pos) => `${identifier}${pos}`

  return [position, formatPosition]
}

const initialState = fromJS({
  module: {},
  solenoid: {},
  slots: {},
  optionalSolenoids: null,
  modificationCount: 0,
})

const handlers = {
  [SOLENOIDS_RECEIVED]: mergeEntity,
  [ECP_MODULES_STATE_RECEIVED]: receivedEcpModulesState,
  [ECP_MODULE_UPDATED]: mergeEntityOnEcpUpdate,
  [REMOVE_SOLENOID]: removeSolenoid,
  [RESET_ECP]: resetEcp,
  [ADD_ECP]: addEcp,
  [ADD_SOLENOID]: addSolenoid,
  [ON_PNEUMATICS_DROP]: onDragDrop,
  [ON_SOLENOID_DRAG]: onSolenoidDrag,
  [OFF_SOLENOID_DRAG]: offSolenoidDrag,
  [ECP_LINKED_FEATURES_RECIEVED]: ecpLinkedFeaturesRecieved,
  [OPTIONAL_SOLENOIDS_NOT_INSTALLED]: addOptionalSolenoids,
  [CLEAR_ECP_STATE]: clearEcpState,
}

export default createReducer(initialState, handlers)

function clearEcpState(state) {
  return state
}

function addOptionalSolenoids(
  state,
  { features, solenoids, currentSolenoids }
) {
  const optionalSolenoids = uniq(
    features.flatMap((item) => item.solenoidIds)
  ).filter((x) => x && !currentSolenoids.includes(x))

  const optionalSolenoidsWithFeatureDetails = uniqBy(
    prop('solenoidId'),
    features.reduce((prev, curr) => {
      if (curr.solenoidIds) {
        return [
          ...prev,
          ...curr.solenoidIds
            .filter((x) => optionalSolenoids.includes(x))
            .map((z, idx) => ({
              solenoidId: z,
              solenoidName: solenoids[z]?.function,
              idx: idx + 1,
              moduleName: curr.moduleName,
              partNumber: solenoids[z]?.partNumber,
              featureNumber: curr.partNumber,
              featureId: curr.id,
              description: curr.description,
              featureRevision: curr.revision,
            })),
        ]
      }
      return prev
    }, [])
  )

  return state.setIn(['optionalSolenoids'], optionalSolenoidsWithFeatureDetails)
}

function mergeEntity(state, { data }) {
  return state.mergeDeep(fromJS(data?.entities))
}

function mergeEntityOnEcpUpdate(state, { data }) {
  const { entities } = data

  const result = state.mergeDeep(fromJS(entities))

  // After successful save, reset changeCount on all slots and modificationCount
  // The API's pending flag is now the source of truth for what needs programming
  // This prevents duplicates from both local changeCount AND API pending flag
  const clearedResult = result
    .update('slots', (slots) => slots.map((slot) => slot.set('changeCount', 0)))
    .set('modificationCount', 0)

  return clearedResult
}

function addEcp(state, { id, serialNumber }) {
  const ecuVariant = state.get('module').first().get('ecuVariant')
  const module = new Map({
    [id]: new Map({
      ecuVariant,
      id,
      maxSolenoids: MAX_SOLENOIDS,
      slots: new Array(MAX_SOLENOIDS)
        .fill(id)
        .map((_item, index) => `SB${id}0${index + 1}`),
      type: 'ecp',
      uri: `/vehicles/${serialNumber}/modules/ecp/${id}`,
    }),
  })
  const slots = new Map({
    ...new Array(MAX_SOLENOIDS).fill(id).reduce((acc, item, index) => {
      const slot = {
        [`SB${item}0${index + 1}`]: Map({
          id: `${index + 1}`,
          location: `SB${item}0${index + 1}`,
          uri: `/vehicles/${serialNumber}/modules/ecp/${item}/slots/${
            index + 1
          }`,
          pendingEdexUpdate: false,
        }),
      }

      return {
        ...acc,
        ...slot,
      }
    }, {}),
  })

  const updatedState = Map({ module, slots })

  return state.mergeDeep(updatedState)
}

function receivedEcpModulesState(state, { ecps, serialNumber }) {
  const merged = state.merge(fromJS(ecps))

  // Always recalculate modificationCount from actual pending data when vehicle data is loaded
  // This ensures the count matches the current vehicle, not data from a previously viewed vehicle
  const slots = merged.get('slots')
  let pendingSlotsCount = 0
  if (slots && slots.size > 0) {
    pendingSlotsCount = slots.filter((slot) => {
      if (!slot.get('uri')) return false
      const hasPendingFlag = slot.get('pending') === true
      const hasLocalChange =
        slot.get('solenoid') !== slot.get('originalSolenoid')
      return hasPendingFlag || hasLocalChange
    }).size
  }

  // Set modificationCount based on actual pending slots from the loaded vehicle
  // This resets the count to match the current vehicle's state
  // Clear optionalSolenoids so stale data from a prior vehicle doesn't leak into
  // notifications — the correct set will arrive via OPTIONAL_SOLENOIDS_NOT_INSTALLED
  return merged
    .set('modificationCount', pendingSlotsCount)
    .set('linkedLogics', Map())
    .set('optionalSolenoids', null)
    .set('activeSerialNumber', serialNumber || null)
}

export function removeSolenoid(state, { slotId }) {
  const [position, formatPosition] = positionUtil(slotId)

  const positionsToUpdate = inclusiveRange(toInt(position), MAX_SOLENOIDS)
  const slotIdsToUpdate = positionsToUpdate.map(formatPosition)

  const editPipeline = slotIdsToUpdate.map((id, index) => (stateDetails) => {
    // Skip slots that don't exist in state (weren't populated from API)
    // This prevents creating partial slot entries that would cause selector issues
    if (!stateDetails.hasIn(['slots', id])) return stateDetails

    const currentSolenoidId = getInOr(
      null,
      ['slots', id, 'solenoid'],
      stateDetails
    )

    // always null for the last slot
    const nextSolenoidId = getInOr(
      null,
      ['slots', slotIdsToUpdate[index + 1], 'solenoid'],
      stateDetails
    )

    // Only increment changeCount if the solenoid value actually changes
    // This prevents inflating count for empty slots that stay empty
    const currentChangeCount = getInOr(
      0,
      ['slots', id, 'changeCount'],
      stateDetails
    )
    const shouldIncrementCount = currentSolenoidId !== nextSolenoidId

    let updatedState = stateDetails.setIn(
      ['slots', id, 'solenoid'],
      nextSolenoidId
    )
    if (shouldIncrementCount) {
      updatedState = updatedState.setIn(
        ['slots', id, 'changeCount'],
        currentChangeCount + 1
      )
    }
    return updatedState
  })

  const updatePartNumbers = (stateDetails) => {
    const getSolenoidId = (id) =>
      compose(tempIdEntity, getIn(['slots', id, 'solenoid']))(stateDetails)

    const removedSolenoidId = getSolenoidId(slotId)
    const solenoidRemoved = getIn(['solenoid', removedSolenoidId], stateDetails)

    if (isEndSolenoidPartNumber(solenoidRemoved)) {
      const slotToUpdate = formatPosition(position - 1)
      const solenoidIdToUpdate = getSolenoidId(slotToUpdate)

      if (!solenoidIdToUpdate) return stateDetails

      return setIn(
        ['solenoid', solenoidIdToUpdate, 'partNumber'],
        END_SOLENOID_PART_NUMBER
      )(stateDetails)
    }

    return stateDetails
  }

  // Increment global modification count for each remove operation
  const currentModCount = state.get('modificationCount') || 0
  const newModCount = currentModCount + 1
  const incrementModCount = (s) => s.set('modificationCount', newModCount)

  const result = pipe(
    updatePartNumbers,
    ...editPipeline,
    incrementModCount
  )(state)

  return result
}

function resetEcp(state, { id }) {
  if (id) {
    const newState = clone(state.toJS())
    delete newState.module[id]
    Object.keys(newState.slots).forEach((slot) => {
      const slotToDelete = `SB${id}`
      if (slot.startsWith(slotToDelete)) {
        delete newState.slots[slot]
      }
    })
    return state.merge(fromJS(newState))
  }
  return state
    .update('slots', (slots) =>
      slots.map((slot) =>
        slot.set('solenoid', slot.get('originalSolenoid')).set('changeCount', 0)
      )
    )
    .set('modificationCount', 0)
}

function addSolenoid(state, { id, slotId }) {
  const [position, formatPosition] = positionUtil(slotId)
  const prevEndSolenoidId = getIn(
    ['slots', formatPosition(position - 1), 'solenoid'],
    state
  )
  const partPath = (solenoidId) => [
    'solenoid',
    tempIdEntity(solenoidId),
    'partNumber',
  ]

  // Increment changeCount to track number of modifications
  const currentChangeCount = getInOr(0, ['slots', slotId, 'changeCount'], state)
  const incrementChangeCount = setIn(
    ['slots', slotId, 'changeCount'],
    currentChangeCount + 1
  )

  // Increment global modification count
  const currentModCount = state.get('modificationCount') || 0
  const newModCount = currentModCount + 1
  const incrementModCount = (s) => s.set('modificationCount', newModCount)

  const addedSolenoid = setIn(['slots', slotId, 'solenoid'], id)
  const updateSolenoidPart = setIn(partPath(id), END_SOLENOID_PART_NUMBER)
  const updatePrevSolenoidPart = setIn(
    partPath(prevEndSolenoidId),
    SOLENOID_PART_NUMBER
  )

  const transform = prevEndSolenoidId
    ? compose(
        incrementModCount,
        addedSolenoid,
        updateSolenoidPart,
        updatePrevSolenoidPart,
        incrementChangeCount
      )
    : compose(
        incrementModCount,
        addedSolenoid,
        updateSolenoidPart,
        incrementChangeCount
      )
  return transform(state)
}

const getSolenoidId = (location, state) =>
  getInOr(null, ['slots', location, 'solenoid'], state)

function onDragDrop(state, { dropItem }) {
  const { toLocation, fromLocation } = dropItem

  const toLocationSolenoidId = getSolenoidId(toLocation, state)
  const fromLocationSolenoidId = getSolenoidId(fromLocation, state)

  if (!toLocationSolenoidId) {
    // Increment changeCount for toLocation since it's receiving a solenoid
    const toLocationChangeCount = getInOr(
      0,
      ['slots', toLocation, 'changeCount'],
      state
    )
    const updatedSlot = state
      .setIn(['slots', toLocation, 'solenoid'], fromLocationSolenoidId)
      .setIn(['slots', toLocation, 'changeCount'], toLocationChangeCount + 1)
    const [position, formatPosition] = positionUtil(fromLocation)
    const positionsToUpdate = inclusiveRange(toInt(position), MAX_SOLENOIDS)
    const slotIdsToUpdate = positionsToUpdate.map(formatPosition)

    const editPipeline = slotIdsToUpdate.map((id, index) => (stateDetails) => {
      // Skip slots that don't exist in state (weren't populated from API)
      if (!stateDetails.hasIn(['slots', id])) return stateDetails

      const currentSolenoidId = getSolenoidId(id, stateDetails)

      // always null for the last slot
      const nextSolenoidId = getSolenoidId(
        slotIdsToUpdate[index + 1],
        stateDetails
      )

      // Only increment changeCount if the solenoid value actually changes
      const currentChangeCount = getInOr(
        0,
        ['slots', id, 'changeCount'],
        stateDetails
      )
      const shouldIncrementCount = currentSolenoidId !== nextSolenoidId

      let updatedState = stateDetails.setIn(
        ['slots', id, 'solenoid'],
        nextSolenoidId
      )
      if (shouldIncrementCount) {
        updatedState = updatedState.setIn(
          ['slots', id, 'changeCount'],
          currentChangeCount + 1
        )
      }
      return updatedState
    })
    const updatePartNumbers = (stateDetails) => {
      const removedSolenoidId = getSolenoidId(fromLocation, stateDetails)
      const solenoidRemoved = getIn(
        ['solenoid', removedSolenoidId],
        stateDetails
      )

      if (isEndSolenoidPartNumber(solenoidRemoved)) {
        const slotToUpdate = formatPosition(position - 1)
        const solenoidIdToUpdate = getSolenoidId(slotToUpdate, stateDetails)

        if (!solenoidIdToUpdate) return stateDetails

        return setIn(
          ['solenoid', solenoidIdToUpdate, 'partNumber'],
          END_SOLENOID_PART_NUMBER
        )(stateDetails)
      }

      return stateDetails
    }

    return pipe(updatePartNumbers, ...editPipeline)(updatedSlot)
  }

  // Swap case: both locations get their solenoids swapped
  const fromLocationChangeCount = getInOr(
    0,
    ['slots', fromLocation, 'changeCount'],
    state
  )
  const toLocationChangeCount = getInOr(
    0,
    ['slots', toLocation, 'changeCount'],
    state
  )

  const transform = compose(
    setIn(['slots', fromLocation, 'solenoid'], toLocationSolenoidId),
    setIn(['slots', fromLocation, 'changeCount'], fromLocationChangeCount + 1),
    setIn(['slots', toLocation, 'solenoid'], fromLocationSolenoidId),
    setIn(['slots', toLocation, 'changeCount'], toLocationChangeCount + 1)
  )

  return transform(state)
}

function onSolenoidDrag(state, { draggingSolenoidItem }) {
  const immDraggingItem = fromJS(draggingSolenoidItem)
  const transform = compose(
    set('isDragging', true),
    set('draggingSolenoidItem', immDraggingItem)
  )
  return transform(state)
}

function offSolenoidDrag(state) {
  const transform = compose(
    set('isDragging', false),
    set('draggingItem', Map())
  )
  return transform(state)
}

function ecpLinkedFeaturesRecieved(
  state,
  { data, moduleType, outputId, serialNumber }
) {
  if (serialNumber && serialNumber !== state.get('activeSerialNumber'))
    return state
  const { features = [] } = data
  return state.setIn(
    ['linkedLogics', `${moduleType}-${outputId}`],
    fromJS(features)
  )
}
